import React from 'react'
import PropTypes from 'prop-types'
import { Accordion } from 'semantic-ui-react'

import { images } from 'packages/simplesat-config'
import TagsAndCommentSettings from 'containers/Admin/Surveys/ZendeskIntegrationSettings/TagsAndCommentSettings'
import SatisfactionRatingBackwardSyncSetting from 'containers/Admin/Surveys/ZendeskIntegrationSettings/SatisfactionRatingBackwardSyncSetting'
import AgentAuthenticationModal from 'containers/Admin/Surveys/ZendeskIntegrationSettings/AgentAuthenticationModal'
import EndUserAuthenticationModal from 'containers/Admin/Surveys/ZendeskIntegrationSettings/EndUserAuthenticationModal'
import ActivateAuthentication from '../../../../organisms/Zendesk/ActivateAuthentication'
import DeActivateAuthentication from '../../../../organisms/Zendesk/DeActivateAuthentication'
import { Condition } from 'components/Condition'
import { IntegrationAccordionTitleContent } from 'components/IntegrationAccordionTitleContent'
import { AccordionAutoIndex } from 'components/AccordionAutoIndex'

const ZendeskAgentAuthentication = ({ surveyId }) => (
  <div>
    <p>
      Once authenticated with Zendesk, Simplesat can update the support ticket after a customer
      submits feedback.
    </p>
    <h5>Example screenshot</h5>
    <img
      className="ui bordered image mb20"
      src={images.zendeskCommentExample}
      alt="zendesk comment rating"
    />
    <AgentAuthenticationModal surveyId={surveyId} />
  </div>
)

ZendeskAgentAuthentication.propTypes = {
  surveyId: PropTypes.string.isRequired,
}

class IntegrationSettings extends React.Component {
  state = { activeIndex: 0 }

  handleClick = (e, titleProps) => {
    const { index } = titleProps
    const { activeIndex } = this.state
    const newIndex = activeIndex === index ? -1 : index

    this.setState({ activeIndex: newIndex })
  }
  render() {
    const {
      match,
      isUpdateTicketTagsAndCommentActive,
      isZendeskSatisfactionRatingBackwardSyncActive,
      history,
    } = this.props

    const { activeIndex } = this.state

    return (
      <div>
        <div className="ui stackable grid">
          <div className="sixteen wide column">
            <div className="ui red message">
              <div className="header">This integration is being deprecated</div>
              <p>
                Simplesat is using a new Zendesk integration method with enhanced functionality.
              </p>
              <button
                onClick={() => history.push('/settings/integrations/595/')}
                className="ui primary button"
              >
                Configure new Zendesk integration
              </button>
              <p>
                <a href="https://help.simplesat.io/en/articles/8389259-zendesk-v1-integration-depreciation-notice">
                  <i className="icon far fa-book" />
                  Learn more about how to migrate to the new Zendesk integrations
                </a>
              </p>
            </div>
          </div>
          <div className="twelve wide column">
            <h3>Configure integrations</h3>
            <p>
              <a
                href="https://help.simplesat.io/en/articles/7154584-zendesk-integrations"
                target="_blank"
                rel="noreferrer"
              >
                <i className="icon far fa-book" />
                Integrating your survey with Zendesk
              </a>
            </p>
          </div>
          <div className="sixteen wide column">
            <Condition condition={isUpdateTicketTagsAndCommentActive}>
              <Condition.True>
                <DeActivateAuthentication surveyId={match.params.surveyId} />
              </Condition.True>
              <Condition.False>
                <ActivateAuthentication surveyId={match.params.surveyId} />
              </Condition.False>
            </Condition>
            <hr />
            <Accordion fluid styled>
              <AccordionAutoIndex activeIndex={activeIndex}>
                <AccordionAutoIndex.Item>
                  <Accordion.Title onClick={this.handleClick}>
                    <IntegrationAccordionTitleContent
                      title="Collect additional Zendesk information"
                      active={isUpdateTicketTagsAndCommentActive}
                      isAutomaticActive
                    />
                  </Accordion.Title>
                  <Accordion.Content>
                    <p className="transition visible">
                      Each time your customer provides a rating, we'll collect the following
                      information associated with the Zendesk ticket:
                    </p>
                    <ul className="transition visible">
                      <li>Requester</li>
                      <li>Organization</li>
                      <li>Assignee</li>
                      <li>Group</li>
                      <li>Subject</li>
                      <li>Channel</li>
                      <li>Custom fields</li>
                    </ul>
                    <p className="transition visible">
                      <em>
                        All information gathered through the API will override fields sent through
                        URL parameters.
                      </em>
                    </p>
                  </Accordion.Content>
                </AccordionAutoIndex.Item>
                <AccordionAutoIndex.Item>
                  <Accordion.Title onClick={this.handleClick}>
                    <IntegrationAccordionTitleContent
                      title="Update a ticket with tags and comments"
                      active={isUpdateTicketTagsAndCommentActive}
                    />
                  </Accordion.Title>
                  <Accordion.Content>
                    {isUpdateTicketTagsAndCommentActive ? (
                      <TagsAndCommentSettings
                        surveyId={match.params.surveyId}
                        hideRemoveIntegration
                      />
                    ) : (
                      <ZendeskAgentAuthentication surveyId={match.params.surveyId} />
                    )}
                  </Accordion.Content>
                </AccordionAutoIndex.Item>
                <AccordionAutoIndex.Item>
                  <Accordion.Title onClick={this.handleClick}>
                    <IntegrationAccordionTitleContent
                      title="Add satisfaction ratings back to Zendesk"
                      active={
                        isZendeskSatisfactionRatingBackwardSyncActive &&
                        isUpdateTicketTagsAndCommentActive
                      }
                    />
                  </Accordion.Title>
                  <Accordion.Content>
                    <p>
                      You can still take advantage of Zendesk's core satisfaction ratings by
                      creating a "fake" end-user account and authenticating with it in Simplesat.
                      Once you do, that account will update ticket ratings and then switch back to
                      the original requester.
                    </p>
                    <h5>Example screenshot</h5>
                    <img
                      className="ui bordered image mb20"
                      src={images.zendeskSatisfactionWriteBack}
                      alt="zendesk satisfaction rating"
                    />

                    {isZendeskSatisfactionRatingBackwardSyncActive ? (
                      <SatisfactionRatingBackwardSyncSetting surveyId={match.params.surveyId} />
                    ) : (
                      <EndUserAuthenticationModal
                        surveyId={match.params.surveyId}
                        disabled={!isUpdateTicketTagsAndCommentActive}
                      />
                    )}
                  </Accordion.Content>
                </AccordionAutoIndex.Item>
              </AccordionAutoIndex>
            </Accordion>
          </div>
        </div>
      </div>
    )
  }
}

IntegrationSettings.propTypes = {
  match: PropTypes.object,
  isUpdateTicketTagsAndCommentActive: PropTypes.bool.isRequired,
  isZendeskSatisfactionRatingBackwardSyncActive: PropTypes.bool.isRequired,
}

IntegrationSettings.defaultProps = {
  isUpdateTicketTagsAndCommentActive: false,
  isZendeskSatisfactionRatingBackwardSyncActive: false,
}

export default IntegrationSettings



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/components/Admin/Surveys/ZendeskIntegrationSettings/index.js